// backgroundPatterns/BackgroundPatternPanel.jsx
//
// Self-contained SVG Patterns panel: a library grid + scale slider + fg/bg color
// controls + live preview. It owns its local editor state and emits a finished
// CSS `background` value (a tiled data:image/svg+xml URL) through `onApply`.
//
// Integration contract (deliberately tiny so the host picker barely changes):
//   <BackgroundPatternPanel value={bgWallpaper} onApply={handleGradientColor} />
// `onApply(cssString)` is the SAME callback the Gradient presets already use
// (-> setBgGradient -> bg_wallpaper, currentBg === 3). So persistence, undo
// history, wheel + per-segment surfaces all work with zero new wiring.

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomSlider from "../SegmentBuilder/Shared/Slider";
import {
  PATTERNS,
  SCALE_MIN,
  SCALE_MAX,
  SCALE_DEFAULT,
  DEFAULT_FG,
  DEFAULT_BG,
  isSafeColor,
} from "./patternGenerators";
import { buildPatternCss, parsePatternCss } from "./toBackgroundCss";
// PATTERN_BG_ENABLED — lazy so the standard-sources picker subtree (SegmentColor +
// usePaletteColor redux/context) is only loaded/mounted on the flag-ON path; the
// flag-OFF default renders the existing raw <input type="color"> controls unchanged.
const PatternColorSources = React.lazy(() => import("./PatternColorSources"));

// Read the flag at RENDER time (not module-eval) so it is test-flippable via
// process.env; canonical declaration lives in constants/featureFlags.js
// (PATTERN_BG_ENABLED). Same env var — identical value at build time.
const patternBgEnabled = () =>
  process.env.REACT_APP_PATTERN_BG === "true" ||
  process.env.REACT_APP_PATTERN_BG === "1";

const swatchCss = (id, { scale, fg, bg }) =>
  // A small fixed-scale preview for the thumbnail grid (independent of the
  // user's chosen scale so motifs stay legible at 44px).
  buildPatternCss({ id, scale: Math.min(scale ?? 18, 18), fg, bg });

// Selected-tile corner badge: a filled purple disc with a white line-art check.
// Gives sighted/low-vision users a clear, non-color-only "this one is chosen" cue
// on top of the ring + aria-pressed (which covers screen readers).
// O4 (Fable UX pass): unify to the brand accent #9B5AF8 (primary500). The prior
// #81459B / #C283DA / #CCA3CF are off-brand purples not on the ladder — the same
// unification SegmentIcon already made; loops/stock/wallpaper libs all use #9B5AF8.
const SELECTED_ACCENT = "#9B5AF8";
const CheckBadge = () => (
  <Box
    aria-hidden="true"
    sx={{
      // Inset into the tile's top-right corner (not overhanging) so the scroll
      // container's clip can never cut it off.
      position: "absolute",
      top: 2,
      right: 2,
      width: 15,
      height: 15,
      borderRadius: "50%",
      background: SELECTED_ACCENT,
      boxShadow: "0 0 0 1.5px #ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 12 12"
      sx={{ width: 10, height: 10, display: "block" }}
    >
      <path
        d="M2.5 6.2 L4.7 8.4 L9.4 3.6"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  </Box>
);

// One square library cell. Shared by the leading "None" tile and every pattern
// swatch so selection chrome (ring + check badge + keyboard/aria wiring) can
// never drift between them. `background` is a ready CSS value ("" for None).
const Swatch = ({ label, selected, dashed, background, onSelect }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-label={label}
    aria-pressed={selected}
    title={label}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    }}
    sx={{
      position: "relative",
      width: "100%",
      aspectRatio: "1 / 1",
      minWidth: 0,
      borderRadius: "6px",
      cursor: "pointer",
      background: background || "#ffffff",
      backgroundSize: "auto",
      border: selected
        ? `2px solid ${SELECTED_ACCENT}`
        : dashed
        ? "1px dashed #b0b4ba"
        : "1px solid #e1e4e8",
      // Stronger, non-color-only selection ring (white inset + purple halo) on
      // top of the border so the chosen tile reads clearly over any tint.
      boxShadow: selected
        ? `0 0 0 2px #ffffff inset, 0 0 0 2px ${SELECTED_ACCENT}`
        : "none",
      outline: "none",
      "&:focus-visible": { boxShadow: "0 0 0 3px rgba(155,90,248,.35)" },
    }}
  >
    {/* "No pattern" affordance for the None tile: a faint diagonal slash. */}
    {dashed && (
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 24 24"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
        preserveAspectRatio="none"
      >
        <path d="M4 20 L20 4" fill="none" stroke="#c7cbd1" strokeWidth="1.5" />
      </Box>
    )}
    {selected && <CheckBadge />}
  </Box>
);

const ColorField = ({ label, value, onChange }) => (
  <Stack spacing={0.5} alignItems="center">
    <Typography sx={{ fontSize: "12px", fontFamily: "DM Sans", color: "#565556" }}>
      {label}
    </Typography>
    <Box
      component="label"
      title={`${label} color`}
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        border: "1px solid #d5d9e0",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        background: value,
        display: "block",
        // O6: keyboard focus is visible — the native <input type="color"> is
        // opacity:0, so without this a keyboard user tabbing here saw nothing.
        "&:focus-within": { boxShadow: "0 0 0 3px rgba(155,90,248,.35)" },
      }}
    >
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          opacity: 0,
          width: "100%",
          height: "100%",
          cursor: "pointer",
          border: "none",
        }}
      />
    </Box>
  </Stack>
);

// A fresh/untouched segment's own fill defaults to white, same as DEFAULT_BG.
// Seeding the line-art color straight from that would make fg === bg and every
// non-"None" pattern tile paint white-on-white (present in the DOM, invisible
// on screen). Normalize to a bare 6-digit lowercase hex so "#fff"/"#FFFFFF"/
// "#ffffff" are all recognized as the same white before comparing.
const normalizeHex = (c) => {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(typeof c === "string" ? c.trim() : "");
  if (!m) return null;
  const hex = m[1].toLowerCase();
  return "#" + (hex.length === 3 ? hex.split("").map((ch) => ch + ch).join("") : hex);
};

const isUsableArtSeed = (c) =>
  isSafeColor(c) &&
  c.trim().toLowerCase() !== "transparent" &&
  normalizeHex(c) !== DEFAULT_BG;

const BackgroundPatternPanel = ({
  value,
  onApply,
  segmentColor,
  // fillHeight: opt-in for hosts that give this panel the full height of a
  // fixed-size container (e.g. PatternPickerDialog's fullScreen mobile popup)
  // instead of letting it flow inline in a page (the Edit-Wheel builder tab,
  // GalleryThemePanel's inline preview). When true, the grid stretches to
  // fill the remaining space via flexbox instead of the fixed 232px cap, so
  // a tall host doesn't leave a large blank area below a short-capped grid.
  fillHeight = false,
}) => {
  const patternBgOn = patternBgEnabled();
  // PATTERN_BG_ENABLED default pair (D4): the LINE-ART color seeds from the SEGMENT's
  // own current fill/palette color when the host supplies a safe, non-white color, and
  // the BACKGROUND seeds white — never the old hardcoded navy. When the segment's own
  // color IS white (or transparent/unsafe), fall back to DEFAULT_FG navy so fg never
  // equals bg. The flag-OFF path keeps the historic navy/white default byte-identical.
  const defaultArt =
    patternBgOn && isUsableArtSeed(segmentColor) ? segmentColor : DEFAULT_FG;

  // Seed editor state from a previously saved pattern descriptor when present.
  const saved = useMemo(() => parsePatternCss(value), [value]);

  const [patternId, setPatternId] = useState(saved?.id || null);
  const [scale, setScale] = useState(saved?.scale || SCALE_DEFAULT);
  const [fg, setFg] = useState(saved?.fg || defaultArt);
  const [bg, setBg] = useState(saved?.bg || DEFAULT_BG);

  // Pre-compute the thumbnail data-URIs once per [scale, fg, bg] change so we
  // don't regenerate every swatch SVG on each unrelated render.
  const swatchById = useMemo(() => {
    const acc = {};
    PATTERNS.forEach((p) => {
      acc[p.id] = swatchCss(p.id, { scale, fg, bg });
    });
    return acc;
  }, [scale, fg, bg]);

  // If the host swaps in a different saved pattern (e.g. switching segments),
  // re-sync the editor to it.
  useEffect(() => {
    if (saved) {
      setPatternId(saved.id);
      if (saved.scale) setScale(saved.scale);
      if (saved.fg) setFg(saved.fg);
      if (saved.bg) setBg(saved.bg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved?.id, saved?.scale, saved?.fg, saved?.bg]);

  // Apply whenever an active pattern's params change. Selecting a pattern is what
  // turns the feature "on" for this background; before that nothing is written.
  const apply = useCallback(
    (next) => {
      const id = next.id ?? patternId;
      if (!id) return;
      const css = buildPatternCss({
        id,
        scale: next.scale ?? scale,
        fg: next.fg ?? fg,
        bg: next.bg ?? bg,
      });
      if (css) onApply(css);
    },
    [patternId, scale, fg, bg, onApply]
  );

  const handleSelect = (id) => {
    setPatternId(id);
    apply({ id });
  };
  // The leading "None" tile: clear the pattern and return the background to the
  // no-pattern state. Emits "" through the SAME onApply the Gradient tab uses,
  // so the host clears bg_wallpaper exactly as an empty selection would.
  const handleClear = () => {
    setPatternId(null);
    onApply("");
  };
  const handleScale = (v) => {
    const n = Math.min(SCALE_MAX, Math.max(SCALE_MIN, Number(v) || SCALE_DEFAULT));
    setScale(n);
    apply({ scale: n });
  };
  const handleFg = (v) => {
    setFg(v);
    apply({ fg: v });
  };
  const handleBg = (v) => {
    setBg(v);
    apply({ bg: v });
  };

  return (
    <Box
      sx={{
        p: 1,
        ...(fillHeight && {
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "14px",
          paddingLeft: "8px",
          marginBottom: "8px",
          fontFamily: "DM Sans",
        }}
      >
        Pattern
      </Typography>

      {/* Dense 6-col scrollable grid (deck: "a dense 6-col scrollable grid,
          with a selectable dashed None tile first"). The grid geometry lives in
          `style` (inline) so the 6-col contract is explicit + test-verifiable,
          not width-driven flex-wrap. In `fillHeight` hosts (the full-screen
          mobile pattern-picker popup) the grid flex-grows to fill the
          available space instead of stopping at the fixed 232px cap that's
          sized for the inline builder-tab context, which otherwise leaves a
          large blank area below a short-capped grid in a tall container. */}
      <Box
        data-testid="pattern-grid-scroll"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: "6px",
        }}
        sx={{
          mb: 1,
          overflowY: "auto",
          pr: 0.5,
          pt: "6px",
          ...(fillHeight
            ? { flex: 1, minHeight: 0 }
            : { maxHeight: 232 }),
        }}
      >
        <Swatch
          label="None"
          dashed
          selected={patternId === null}
          onSelect={handleClear}
        />
        {PATTERNS.map((p) => (
          <Swatch
            key={p.id}
            label={p.label}
            selected={patternId === p.id}
            background={swatchById[p.id]}
            onSelect={() => handleSelect(p.id)}
          />
        ))}
      </Box>

      {patternId && (
        <>
          {/* Live preview */}
          <Box
            aria-label="Pattern preview"
            sx={{
              height: 96,
              borderRadius: "8px",
              border: "1px solid #e1e4e8",
              mb: 1.5,
              background: buildPatternCss({ id: patternId, scale, fg, bg }),
              backgroundSize: "auto",
            }}
          />

          <Stack mx={1} sx={{ mb: 1 }}>
            <CustomSlider
              title="Pattern size"
              symbol="px"
              value={scale}
              onChange={handleScale}
              min={SCALE_MIN}
              max={SCALE_MAX}
              filledColor="#9B5AF8"
              thumbColor="#9B5AF8"
              bgColor="#E2D5FF"
            />
          </Stack>

          {patternBgOn ? (
            // PATTERN_BG_ENABLED: standard-sources pickers (line-art + background)
            // + explicit-transparent swatch, replacing the two raw color inputs.
            <React.Suspense fallback={null}>
              <PatternColorSources
                art={fg}
                bg={bg}
                onArtChange={handleFg}
                onBgChange={handleBg}
              />
            </React.Suspense>
          ) : (
            <Stack
              direction="row"
              spacing={3}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <ColorField label="Foreground" value={fg} onChange={handleFg} />
              <ColorField label="Background" value={bg} onChange={handleBg} />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default BackgroundPatternPanel;
